import { View, Text, type StyleProp, type ViewStyle } from "react-native";
import { HelpCircle } from "lucide-react-native";
import { CardSurface, SectionTitle } from "@louise/moon-ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@louise/moon-ui/primitives/Accordion";
import { useRegion, useTranslations, type Region } from "@src/packages/i18n";
import { useUserCountry } from "@src/packages/location/useUserCountry";
import { EMBER } from "../../../onboarding/theme";

/**
 * Each list answers questions about one country's system, so the region picks
 * the list and the app language picks the words. There is no UK list yet, and
 * no FAQ is better than another country's rules, so the UK shows none.
 */
const FAQ_KEYS_BY_REGION: Record<Region, readonly string[]> = {
  US: [
    "faq.us.insurance_does_not_cover_fertility",
    "faq.us.how_long_insurance_verification_takes",
    "faq.us.can_hsa_or_fsa_pay_for_treatment",
    "faq.us.how_accurate_cost_estimates_are",
  ],
  FR: [
    "faq.france.what_amp_coverage_includes",
    "faq.france.what_louise_does_in_france",
    "faq.france.who_can_access_amp",
    "faq.france.is_prior_approval_required",
    "faq.france.age_limit_for_amp",
  ],
  UK: [],
};

/**
 * The app's "FR" region also covers every country that isn't the US or the UK,
 * but the French answers (AMP, Assurance Maladie) only hold where that system
 * applies: France and its overseas departments.
 */
const FRENCH_SYSTEM_COUNTRIES = new Set(["FR", "GP", "MQ", "GF", "RE", "YT"]);

export default function FaqSection({
  style,
}: {
  style?: StyleProp<ViewStyle>;
}) {
  const region = useRegion();
  const { countryCode } = useUserCountry();
  const t = useTranslations();

  if (region === null) return null;
  const keys =
    region === "FR" && !FRENCH_SYSTEM_COUNTRIES.has(countryCode ?? "")
      ? []
      : FAQ_KEYS_BY_REGION[region];
  if (keys.length === 0) return null;

  return (
    <View style={style}>
      <View style={{ marginBottom: 12, paddingHorizontal: 2 }}>
        <SectionTitle icon={HelpCircle}>
          {t("faq.frequently_asked")}
        </SectionTitle>
      </View>

      <CardSurface style={{ paddingHorizontal: 18, paddingVertical: 6 }}>
        <Accordion type="single" collapsible>
          {keys.map((key) => (
            <AccordionItem key={key} value={key}>
              {/* The question copy changes with region and language, so the
                  stable identifier is the control, not the visible string. */}
              <AccordionTrigger action="faq_item">
                {t(`${key}.question`)}
              </AccordionTrigger>
              <AccordionContent>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    color: EMBER.colors.textLight,
                    lineHeight: 18,
                  }}
                >
                  {t(`${key}.answer`)}
                </Text>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </CardSurface>
    </View>
  );
}
